import React, {createContext, useContext, useEffect, useState,} from 'react';
import {useDispatch} from 'react-redux';
import * as actions from '../actions/TabDocumentsActions';
import {useOldInspectionDetailsContext} from './OldInspectionsDetailsContext';
import {closeShuma} from '../../_common/actions/oldInspectionDetailsActions';
import {useFileTypesOptionsContext} from '../../_common/components/FileTypesOptionsContext/FileTypesOptionsContext';
import AnimatedUploadingFilePopUp from '../../_common/components/AnimatedUploadingFilePopUp';
import {showGlobalMessage} from '../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {messages as M} from '../../../../_metronic/i18n/messages';

export type FileData = {
  fileID: number
  shumaID: number
  fileName: string
  fileExtension: string
  stepID: number
  fileTypeID: number
  fileTypeName: string
  uploadTime: Date
  freeText: string
  folderID: number
  folderName: string
  uploadedByUserID: number
  uploadedByUserFirstName: string
  uploadedByUserLastName: string
  link: string
  thumbnail: string
}

type OldInspectionDocumentsContext = {
  fileList: FileData[],
  uploadFiles: (files: File[], fileTypeID: number) => Promise<void>,
  handleDownloadFile: (fileID: number, fileName: string) => void,
  handleDeleteFile: (fileID: number) => void,
  handleUpdateFileType: (fileID: number, fileTypeID: number) => void,
  handleViewFile: (fileID: number) => void,
  fileTypesOptions: {[key: string]: unknown}[],
  updateEditedFile: (fileID: number, file: Blob) => void
  updateFileList: () => void
}

const OldInspectionDocumentsContext = createContext<OldInspectionDocumentsContext>({} as OldInspectionDocumentsContext);

export function useOldInspectionDocumentsContext() {
  return useContext(OldInspectionDocumentsContext);
}

interface OldInspectionDocumentsProviderProps {
  children: React.ReactNode;
}

export function OldInspectionDocumentsProvider({children}: OldInspectionDocumentsProviderProps) {
  const dispatch = useDispatch();
  const [uploadingFile, setUploadingFile] = useState(false);
  const {fileTypes: fileTypesOptions} = useFileTypesOptionsContext();
  const [fileList, setFileList] = useState<FileData[]>([]);
  //@ts-ignore
  const {shumaID} = useOldInspectionDetailsContext();

  const updateFileList = async () => {
    const result = await dispatch<any>(actions.getFileList(shumaID));
    const array = Array.isArray(result) ? result : [];
    setFileList(array);
  };

  useEffect(() => {
    if (shumaID && shumaID !== -1) {
      updateFileList();
    }
  }, []);

  const uploadFiles = async (files: File[], fileTypeID: number) => {
    setUploadingFile(true);
    const calls = files
      .map(async (file) => await actions.uploadFile(file, shumaID, fileTypeID, file.name));
    const results = await Promise.all(calls);
    if (results) {
      await updateFileList();
      document.body.click();
      if (fileTypeID === 21) {
        dispatch(closeShuma(shumaID));
      }
    }
    dispatch(showGlobalMessage(
      results ? 'success' : 'error',
      M[results ? '0' : '-1'])
    );
    setTimeout(() => setUploadingFile(false), 1000);
  };

  const handleDownloadFile = (fileID: number, fileName: string) => {
    dispatch(actions.getFile({shumaID, fileID, fileName}));
  };
  const handleViewFile = (fileID: number) => {
    if (shumaID) {
      return actions.getFilePreview({shumaID, fileID});
    }
  };
  const handleDeleteFile = async (fileID: number) => {
    dispatch<any>(actions.deleteFile({shumaID, fileID}))
      .then(updateFileList);
  };
  const handleUpdateFileType = async (fileID: number, fileTypeID: number) => {
    await actions.updateFileType(fileID, fileTypeID);
    await updateFileList();
  };

  const updateEditedFile = async (fileID: number, file: Blob) => {
    const [fileData] = fileList.filter((file) => file.fileID === fileID);
    if (fileData) {
      await dispatch<any>(actions.deleteFile({shumaID, fileID}));
      await actions.uploadFile(file, shumaID, fileData.fileTypeID, fileData.fileName);
    }
  };

  return (
    <>
      <AnimatedUploadingFilePopUp show={uploadingFile}/>
      <OldInspectionDocumentsContext.Provider value={{
        fileList,
        uploadFiles,
        handleDownloadFile,
        handleDeleteFile,
        handleUpdateFileType,
        handleViewFile,
        fileTypesOptions,
        updateEditedFile,
        updateFileList,
      }}>
        {children}
      </OldInspectionDocumentsContext.Provider>
    </>
  );
}
